import { useSessionStorage } from "@uidotdev/usehooks";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import APIS from "../../constant/EndPoint";
import { IFilterPickup } from "../../types/interface";
import { hasValues } from "../../utils/hasValues";
import Card from "../Reusable/Card";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import PickupRequestFilter from "../InternationalPickupRequest/PickupRequestFilter";
import { tw } from "@careinfo/tailwind";
import Modal from "../Reusable/Modal";
import { Remark } from "@careinfo/layout";
import { ColumnDef } from "@tanstack/react-table";
import { ClientAccessControl } from "@careinfo/permission-required";

const PickupNoteList = () => {
  const navigate = useNavigate();
  const [openIsCompletePopup, setOpenisCompletePopup] = useState(false);
  const [localState, handleSetState] = useSessionStorage<IFilterPickup>(
    "pickup-note",
    {
      created__range: null,
      vehicle: null,
      driver: null,
      merchant: null,
    }
  );
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState("");
  const [receiver, setReceiver] = useState("");
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));
  const { setValue, getValues, watch, reset, control } = useForm<IFilterPickup>(
    {
      defaultValues: async () => ({
        created__range: localState?.created__range ?? null,
        vehicle: localState?.vehicle ?? null,
        driver: localState?.driver ?? null,
        merchant: localState?.merchant ?? null,
      }),
    }
  );
  const handleRecevierName = (row: any) => {
    setReceiver(row);
  };
  const handleFilterReset = () => {
    reset({
      created__range: null,
      vehicle: null,
      driver: null,
      merchant: null,
    });
  };

  const defaultCargoColumns: ColumnDef<any, any>[] = [
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <div className={tw`cursor-pointer focus:outline-none`}>
            <p className={tw`whitespace-nowrap`}>{getValue() ?? "N/A"}</p>
            <p className={tw`text-xs text-red-500`}>
              {row?.original?.invoice_print_count ? "Printed" : null}
            </p>
          </div>
        );
      },
    },

    {
      header: "Date",
      accessorKey: "created",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            {getValue() ? (
              <p className={tw`max-w-[80px]`}>
                {moment(getValue()).format("YY-MM-DD HH:mm:ss")}
              </p>
            ) : (
              "N/A"
            )}
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <div>
              <p>{getValue()}</p>
              <p className={tw`text-[12px]`}>
                {row?.original?.sender_primary_contact}
              </p>
              <p className={tw`text-[12px]`}>{row?.original?.sender_address}</p>
            </div>
          </>
        );
      },
    },
    {
      header: "Receiver",
      accessorKey: "receiver_name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <div>
              <p>{getValue()}</p>
              <p className={tw`text-[12px]`}>
                {row?.original?.receiver_primary_contact}
              </p>
            </div>
          </>
        );
      },
    },
    {
      header: "Receiver Address",
      accessorKey: "receiver_address",
      cell: NACell,
    },
    {
      header: "Piece",
      accessorKey: "piece",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    {
      header: "Weight",
      accessorKey: "weight",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p>{getValue() ?? 0.0}</p>
          </>
        );
      },
    },
    {
      header: "Remarks",
      accessorKey: "remark",
      cell: ({ row, getValue }: any) => (
        <>
          <ClientAccessControl
            children={
              <button
                className={tw`focus:outline-none`}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedOrder(row?.original?.id);
                  setOpenReviewModal(true);

                  handleRecevierName(row?.original?.receiver_name);
                }}
              >
                {getValue()} Remarks
              </button>
            }
            allowedPermissions={["can_view_remarks"]}
            renderNoAccess={<p>{getValue()} Remarks</p>}
          />
        </>
      ),
    },
    {
      header: "Status",
      accessorKey: "is_pickup_note_complete",
      cell: ({ getValue, row }: any) => {
        return (
          <>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrder(row?.original?.id);
                setOpenisCompletePopup(true);
              }}
            >
              {getValue() ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86M22 4L12 14.01L9.00001 11.01"
                    stroke="#26C943"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 9L9 15M9 9L15 15M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                    stroke="red"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              )}
            </div>
          </>
        );
      },
    },
  ];

  const handleEditNavigate = (row: any) => {
    navigate(`edit/${row?.original?.id}`);
  };

  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]);

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  return (
    <>
      <DataTable
        url={`${APIS.PICKUP_NOTES}`}
        queryKey={"pickup-note"}
        handleEditNavigate={handleEditNavigate}
        columns={defaultCargoColumns}
        // orderSearch={true}
        tableName={"pickup-note"}
        addNewPagePath={"create"}
        params={{ ...params() }}
      />
      <Modal
        isOpen={openReviewModal}
        setIsOpen={setOpenReviewModal}
        title={"Remark"}
      >
        <div className={tw`w-[600px]`}>
          <Remark selectedOrder={selectedOrder} receiver={receiver}/>
        </div>
      </Modal>
      <Modal
        isOpen={openIsCompletePopup}
        setIsOpen={setOpenisCompletePopup}
        title="Mark as done"
      >
        <div
          className={tw`flex flex-col items-center justify-center  h-fit mt-4`}
        >
          <p className={tw`text-md  mb-4 `}>
            Are you sure you want to mark this pickup note as done?
          </p>
          <div className={tw`mt-6 flex justify-center gap-x-6 items-start`}>
            <div className={tw`w-24`}>
              <button
                type="button"
                className={tw`w-full px-[24px] py-2 rounded  focus:outline-none bg-primary  text-white hover:bg-blue-400`}
                onClick={() => {
                  window.location.replace(`order#/list/edit/${selectedOrder}`);
                }}
              >
                {" "}
                Yes
              </button>
            </div>
            <div>
              <button
                type="button"
                className={tw`w-full px-[24px] py-2 rounded  focus:outline-none bg-[#EEF5F4] hover:bg-[wheat]`}
                onClick={() => setOpenisCompletePopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PickupNoteList;

